import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useRiskRecognitionHighlight } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useRightPanelStore from '@cobliteam/hooks/src/useRightPanelStore/useRightPanelStore';
import { type TransitionEvent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { NotificationCardRender } from '../../../core/NotificationType';
import useNotificationDrawerStore from '../../../core/store/useNotificationDrawerStore';
import { buildRiskRecognitionEventProps, RISK_RECOGNITION_EVENTS } from '../analytics';
import { isHighlightRenderable, RISK_DRAWER_PARAM } from '../parse';
import RiskCardBody from '../RiskCardBody';
import useRiskRecognitionSlice from '../store/useRiskSlice';

const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';

// Fire "Displayed" once per bucket. Reads the slice via getState (not a hook) since it runs inside an effect.
const trackDisplayedOncePerBucket = (highlight: RiskRecognitionHighlight) => {
  const { dismissedBucketEnd, lastDisplayedBucketEnd, markDisplayed } = useRiskRecognitionSlice.getState();
  if (!isHighlightRenderable(highlight, dismissedBucketEnd) || highlight.time_bucket_end === lastDisplayedBucketEnd) return;
  Analytics.track(RISK_RECOGNITION_EVENTS.displayed, buildRiskRecognitionEventProps(highlight, 'card'));
  markDisplayed(highlight.time_bucket_end);
};

/**
 * Controller da surface "card flutuante" do risco. Resolve o highlight atual via
 * `useRiskRecognitionHighlight` (`/v1/risk-recognitions/highlights`), gateia só por flag (aparece em todas
 * as páginas), trata a animação de saída + analytics, e devolve o descritor de render pra moldura genérica
 * — ou `null` quando não há nada a mostrar. Supressão é local (store) + silence.
 */
const useRiskCardController = (): NotificationCardRender | null => {
  const { knockCardRiskRecognition } = useFeatureFlags();
  const { search } = useLocation();
  const history = useHistory();

  const dismissedBucketEnd = useRiskRecognitionSlice((s) => s.dismissedBucketEnd);
  const dismiss = useRiskRecognitionSlice((s) => s.dismiss);
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  const isRightPanelHidden = useRightPanelStore((s) => s.isRightPanelHidden);

  // Gate só por flag — o card aparece em todas as páginas (poll de `/v1/risk-recognitions/highlights`,
  // RiskRecognitionsRequester). Sem mais restrição de página nem lógica de seguir trajeto no /tracking.
  const { data: highlight } = useRiskRecognitionHighlight(knockCardRiskRecognition);

  const canRenderHighlight = knockCardRiskRecognition && isHighlightRenderable(highlight, dismissedBucketEnd) && !!highlight?.metadata;

  // Keyed by bucket (not a boolean) so the next bucket's fresh highlight never inherits a stale "exiting"
  // flag and auto-dismisses.
  const [exitingBucketEnd, setExitingBucketEnd] = useState<string | null>(null);

  // Fire "Displayed" once per bucket as a renderable highlight arrives. Custom surfaces own their analytics;
  // there is no feed engagement anymore (markAsSeen left with the Knock feed) — suppression is local + silence.
  useEffect(() => {
    if (canRenderHighlight && highlight) trackDisplayedOncePerBucket(highlight);
  }, [canRenderHighlight, highlight]);

  if (!(canRenderHighlight && highlight)) return null;

  const isExiting = exitingBucketEnd === highlight.time_bucket_end;

  const handleDismiss = () => {
    // Fire the personal silence (~2h) in parallel with the exit animation. Guarded: without a detection_id
    // (backend not yet shipping it) we still hide locally — `silenced` records whether the silence POST went out.
    const detectionId = highlight.metadata?.detection_id;
    Analytics.track(RISK_RECOGNITION_EVENTS.dismissed, { ...buildRiskRecognitionEventProps(highlight, 'card'), silenced: Boolean(detectionId), silenceReason: 'dismissed' });
    if (detectionId) createSilence({ detectionId, reason: 'dismissed' });
    setExitingBucketEnd(highlight.time_bucket_end);
  };

  const handleExitEnd = (event: TransitionEvent<HTMLDivElement>) => {
    // The opacity leg settles the dismissal; the transform leg fires its own transitionend we ignore.
    // Dismiss the captured exiting bucket, not the live highlight — a new bucket may have arrived mid-animation.
    if (event.propertyName !== 'opacity' || !exitingBucketEnd) return;
    dismiss(exitingBucketEnd);
    setExitingBucketEnd(null);
  };

  const handleAction = () => {
    Analytics.track(RISK_RECOGNITION_EVENTS.action, buildRiskRecognitionEventProps(highlight, 'card'));
    // Abre o drawer pelo store (conteúdo imediato) E reflete na URL (`?risk-recognition-drawer=<id>`) pra
    // virar deep link compartilhável. O host do drawer não re-busca: o store já tem o payload.
    openDrawer(RISK_RECOGNITION_TYPE_KEY, highlight);
    const params = new URLSearchParams(search);
    // Deep link pelo id da notificação; o loader do drawer resolve pela listagem (1ª página) num reload.
    params.set(RISK_DRAWER_PARAM, highlight.id);
    history.push({ search: `?${params.toString()}` });
  };

  // Labels dos botões caem no i18n do `RiskCardBody` (`risk_recognition.action`/`.dismiss`) — não vêm mais
  // do `button_set` do feed do Knock.
  return {
    isExiting,
    isRightPanelHidden,
    onTransitionEnd: handleExitEnd,
    body: <RiskCardBody highlight={highlight} onDismiss={handleDismiss} onAction={handleAction} />,
  };
};

export default useRiskCardController;
